import React, {useLayoutEffect, useRef, useState} from "react";
import DataGridBodyCell from "./cell";
import {
    columnWidthPx,
    HEIGHT_PROBE_ATTR,
    HEIGHT_PROBE_ROW_ATTR,
    measureCellContribution,
    MEASURE_COLUMN_ATTR,
} from "./virtual-column-heights";

const noop = () => undefined;

/**
 * Renders one row's unmeasured element columns in a hidden table and reports each cell's content height.
 * The host row is zero-height so it does not change the grid's scroll size.
 * @param {object | null} request
 * @param {Function} createCells
 * @param {string} density
 * @param {number} minRowHeight
 * @param {Function} getColWidth
 * @param {number} colSpan
 * @param {Function} onMeasured
 * @return {JSX.Element | null}
 */
const ColumnHeightProbe = ({
                                request,
                                createCells,
                                density,
                                minRowHeight,
                                getColWidth,
                                colSpan,
                                onMeasured,
                            }) => {
    const rootRef = useRef(null);
    const requestRef = useRef(request);
    requestRef.current = request;
    const measureKey = request
        ? `${request.row?.key}:${request.columns.map((column) => `${column.name}@${columnWidthPx(column, getColWidth)}`).join("|")}`
        : "";
    const [attempt, setAttempt] = useState(0);
    const attemptKeyRef = useRef(measureKey);
    if (attemptKeyRef.current !== measureKey) {
        attemptKeyRef.current = measureKey;
        if (attempt !== 0) {
            setAttempt(0);
        }
    }

    /**
     * Reads the probe after layout, then the parent schedules the next row on a later frame.
     * A zero-height pass is retried so a not-yet-laid-out probe does not lock the row short.
     */
    useLayoutEffect(() => {
        const current = requestRef.current;
        if (!current || !rootRef.current) {
            return undefined;
        }
        const measurements = [];
        rootRef.current.querySelectorAll(`td[${MEASURE_COLUMN_ATTR}]`).forEach((td) => {
            if (!(td instanceof HTMLElement)) {
                return;
            }
            measurements.push({
                columnName: td.getAttribute(MEASURE_COLUMN_ATTR),
                height: measureCellContribution(td),
            });
        });
        const laidOut = measurements.length > 0 && measurements.some((measurement) => measurement.height > 0);
        if (!laidOut && attempt < 4) {
            const frame = window.requestAnimationFrame(() => setAttempt((value) => value + 1));
            return () => window.cancelAnimationFrame(frame);
        }
        if (measurements.length) {
            onMeasured(current.row, measurements);
        }
        return undefined;
    }, [measureKey, onMeasured, attempt]);

    if (!request?.columns?.length) {
        return null;
    }

    const cells = createCells(request.columns, request.row) ?? [];

    return (
        <tr {...{[HEIGHT_PROBE_ATTR]: ""}} aria-hidden style={{height: 0}}>
            <td colSpan={colSpan || 1} style={{padding: 0, border: 0, height: 0}}>
                <div
                    ref={rootRef}
                    style={{
                        position: "fixed",
                        left: 0,
                        top: 0,
                        visibility: "hidden",
                        pointerEvents: "none",
                    }}
                >
                    {cells.map((cell) => {
                        const width = columnWidthPx(cell.column, getColWidth);
                        return (
                            <table
                                key={cell.column.name}
                                style={{
                                    tableLayout: "fixed",
                                    width,
                                    borderCollapse: "separate",
                                    borderSpacing: 0,
                                }}
                            >
                                <tbody className="data-grid-body">
                                <tr className="data-grid-body-row" {...{[HEIGHT_PROBE_ROW_ATTR]: ""}}>
                                    <DataGridBodyCell
                                        density={density}
                                        cell={cell}
                                        rowKey={request.row?.key}
                                        rowSelected={false}
                                        hasDetailedPanel={false}
                                        detailedPanelVisible={false}
                                        detailedPanelOpen={false}
                                        toggleSelection={noop}
                                        setDetailedPanelOpen={noop}
                                        groupedRowsVisible={false}
                                        groupedRowsOpen={false}
                                        setGroupedRowsOpen={noop}
                                        startScrolling={noop}
                                        tdStyle={{
                                            width,
                                            minWidth: width,
                                            maxWidth: width,
                                            minHeight: minRowHeight,
                                        }}
                                    />
                                </tr>
                                </tbody>
                            </table>
                        );
                    })}
                </div>
            </td>
        </tr>
    );
};

export default ColumnHeightProbe;
